import { ChangeEvent, FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { CircularProgress } from "@mui/material";

import { Link } from "react-router-dom";

import { InputField } from "@/components/InputField/InputField";
import { CustomColumnTypes } from "@/enum/list.enum";
import { classNames } from "@/utils/classNames";
import { runOrToast } from "@/utils/runOrToast";

import { useUpdateCostumColumnMutation } from "@/pages/Private/redux/list/list.api";

import { LinkedinIcon } from "@/components/LinkedinIcon/LinkedinIcon";

import { Tooltip } from "@/components/Tooltip/Tooltip";

import { ListPerson } from "../../schema/list";
import { ColumnValue } from "../../schema/columnValue";
import { ColumnConfig } from "../../schema/columnConfig";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const readCellValue = (cVal: any) => {
	try {
		return cVal?.answer !== undefined && cVal?.answer !== null
			? cVal?.answer?.toString()
			: cVal?.title || cVal?.commentary || cVal || "";
	} catch (e) {
		console.log(e);
	}
};

export interface CustomDepartmentSizeColumnProps {
	showEdit?: { id: number; name: string };
	columnValue?: ColumnValue;
	columnConfig: ColumnConfig;
	row: ListPerson;
	customColumnNumberValue?: number | null;
	setCustomColumnNumberValue: (value: number | null) => void;
	setShowEdit: (value: { id: number; name: string }) => void;
	type: CustomColumnTypes;
}

export const CustomDepartmentSizeColumn: FunctionComponent<CustomDepartmentSizeColumnProps> = ({
	showEdit,
	columnValue,
	columnConfig,
	row,
	customColumnNumberValue,
	setCustomColumnNumberValue,
	setShowEdit,
	type,
}) => {
	const [updatePerson, { isLoading }] = useUpdateCostumColumnMutation();
	const { t } = useTranslation();

	const [value, setValue] = useState<number | null>(null);

	useEffect(() => {
		if (columnValue?.value) {
			setValue(readCellValue(columnValue.value));
		} else if (customColumnNumberValue) {
			setValue(customColumnNumberValue);
		}
	}, [columnValue, customColumnNumberValue]);

	if (isLoading) {
		return <CircularProgress />;
	}

	if (showEdit?.id === row.id && showEdit?.name === columnConfig.name) {
		return (
			<InputField
				autoFocus
				handleBlur={() => {
					setShowEdit({ id: 0, name: "" });
				}}
				handleChange={function (event: ChangeEvent<HTMLInputElement>): void {
					setValue(+event.target.value || null);
				}}
				name={columnConfig.name}
				type="number"
				value={value || ""}
				onKeyDown={async (e) => {
					if (e.key === "Enter") {
						const submitted = value || null;

						// Close this cell before the save settles: `showEdit` is shared by every cell,
						// so clearing it after the await would close a cell the user opened meanwhile
						// (PREC-4840). Only this cell's local rollback follows the await.
						setCustomColumnNumberValue(null);
						setShowEdit({ id: 0, name: "" });
						const saved = await runOrToast(
							() =>
								updatePerson({
									id: row.id,
									columnId: columnConfig.id,
									customColumnName: columnConfig.name,
									value: submitted,
									customColumnType: type,
								}).unwrap(),
							t("errors.saveFailed")
						);

						if (!saved) {
							setValue(columnValue?.value ? readCellValue(columnValue.value) : null);
						}
					}
				}}
			/>
		);
	} else {
		return (
			<Tooltip
				content={
					row.prospect?.organization?.estimatedNumEmployees && value && value > 0
						? `${((value / row.prospect.organization.estimatedNumEmployees) * 100).toFixed(
								2
							)}% of all employees`
						: ""
				}
				disabled={!row.prospect?.organization?.estimatedNumEmployees || !!(value && value <= 0)}
			>
				<div className="flex flex-row items-center gap-2">
					<button
						onClick={(e) => {
							e.preventDefault();
							e.stopPropagation();
							// setCustomColumnNumberValue(columnValue?.value ? (columnValue.value as number) : null);

							setShowEdit({ id: row.id, name: columnConfig.name });
						}}
					>
						{
							<div
								className={classNames(
									"h-[26px] min-w-[100px] cursor-pointer hover:bg-brand-light hover:opacity-[0.7] leading-tight hover:text-white rounded p-1",
									"text-right"
								)}
							>
								{value}
							</div>
						}
					</button>
					{value && value > 0 && (
						<Link
							target="_blank"
							to={`https://www.linkedin.com/search/results/people/?currentCompany=%5B%22${
								row.prospect?.organization?.companyLinkedinId
								// eslint-disable-next-line @typescript-eslint/no-explicit-any
							}%22%5D&keywords=${(JSON.parse(columnConfig.config) as any[])
								.map((e) => `"${e}"`)
								// eslint-disable-next-line @typescript-eslint/no-explicit-any
								.join(" OR ")}&titleFreeText=${(JSON.parse(columnConfig.config) as any[])
								.map((e) => `"${e}"`)
								.join(" OR ")}&origin=GLOBAL_SEARCH_HEADER&sid=XC.`}
							onClick={(e) => {
								e.stopPropagation();
							}}
						>
							<LinkedinIcon />
						</Link>
					)}
				</div>
			</Tooltip>
		);
	}
};
